import React from "react";
import {
  ComposableMap,
  Geographies,
  Geography,
  ZoomableGroup,
} from "react-simple-maps";
import { customColors } from "../util/Theme";

/* ---------------- types ---------------- */

type Position = { coordinates: [number, number]; zoom: number };

/* ---------------- config ---------------- */

const WORLD_POSITION: Position = { coordinates: [0, 20], zoom: 1 };
const MIN_ZOOM = 1;
const MAX_ZOOM = 8;

/* ---------------- component ---------------- */

export const WorldMap = React.memo(
  (props: {
    setTooltipContent: React.Dispatch<React.SetStateAction<React.ReactNode>>;
    setTooltipStyle: React.Dispatch<React.SetStateAction<React.CSSProperties>>;
  }) => {
    const [position, setPosition] = React.useState<Position>(WORLD_POSITION);

    // counter-scale strokes so they stay crisp when zoomed in
    const inv = 1 / position.zoom;

    return (
      <div style={containerStyle}>
        <ComposableMap height={470}>
          <ZoomableGroup
            center={position.coordinates}
            zoom={position.zoom}
            minZoom={MIN_ZOOM}
            maxZoom={MAX_ZOOM}
            onMoveEnd={(p) =>
              setPosition({
                coordinates: p.coordinates as [number, number],
                zoom: p.zoom,
              })
            }
          >
            <Geographies geography="/features.json">
              {({ geographies }) =>
                geographies.map((geo) => {
                  const visited = !!geo.properties.visited;
                  return (
                    <Geography
                      data-tooltip-id="my-tooltip"
                      key={geo.rsmKey}
                      geography={geo}
                      onMouseEnter={() => {
                        if (geo.properties.note) {
                          props.setTooltipContent(
                            <>
                              <strong>{geo.properties.name}</strong>
                              <br />
                              <span>{geo.properties.note}</span>
                            </>
                          );
                        } else {
                          props.setTooltipContent(`${geo.properties.name}`);
                        }
                        if (visited) {
                          props.setTooltipStyle({
                            backgroundColor: customColors.foreground,
                            color: customColors.background,
                          });
                        } else {
                          props.setTooltipStyle({
                            backgroundColor: customColors.background,
                            color: customColors.foreground,
                          });
                        }
                      }}
                      onMouseLeave={() => {
                        props.setTooltipContent("");
                        props.setTooltipStyle({ display: "none" });
                      }}
                      style={{
                        default: {
                          fill: visited ? customColors.foreground : customColors.background,
                          stroke: visited ? customColors.background : customColors.foreground,
                          strokeWidth: 0.5 * inv,
                          outline: "none",
                          transition: "fill 0.2s",
                        },
                        hover: {
                          fill: visited ? "#333333" : "#f1f1f1",
                          stroke: visited ? customColors.background : customColors.foreground,
                          strokeWidth: 0.5 * inv,
                          outline: "none",
                          cursor: "pointer",
                        },
                        pressed: {
                          fill: visited ? customColors.foreground : customColors.background,
                          stroke: visited ? customColors.background : customColors.foreground,
                          strokeWidth: 0.5 * inv,
                          outline: "none",
                        },
                      }}
                    />
                  );
                })
              }
            </Geographies>
          </ZoomableGroup>
        </ComposableMap>
      </div>
    );
  }
);

/* ---------------- styles ---------------- */

const containerStyle: React.CSSProperties = {
  position: "relative",
  border: `1px solid ${customColors.foreground}`,
  background: customColors.background,
};

/* grab cursor for the pannable SVG */
if (typeof document !== "undefined" && !document.getElementById("worldmap-cursor-style")) {
  const style = document.createElement("style");
  style.id = "worldmap-cursor-style";
  style.innerHTML = `
    .rsm-svg { cursor: grab; }
    .rsm-svg:active { cursor: grabbing; }
  `;
  document.head.appendChild(style);
}
